import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, ImageOff, CheckCircle2, Circle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADES = ['K', '1', '2', '3-4', '5-6', '7-8', '9-12'];
const MODALITIES = ['listening', 'reading', 'writing', 'speaking'];

export default function ContentIntegrityCatalogue() {
  const [grade, setGrade] = useState('K');
  const [modality, setModality] = useState('reading');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  const [cheatBusy, setCheatBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/content-integrity/catalogue`, {
        params: { grade, modality, limit: 50, only_with_image: true },
        withCredentials: true,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load catalogue');
    }
    setLoading(false);
  }, [grade, modality]);

  useEffect(() => { load(); }, [load]);

  const download = async (path, filename) => {
    const busySetter =
      path.includes('cheatsheet') ? setCheatBusy :
      path.includes('terminology') ? setAuditBusy : setPdfBusy;
    busySetter(true);
    try {
      const res = await axios.get(`${API}${path}`, {
        params: (path.includes('terminology') || path.includes('cheatsheet'))
          ? {}
          : { grade, modality, limit: 50, only_with_image: true },
        responseType: 'blob',
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('PDF export failed');
    }
    busySetter(false);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="content-integrity-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="ci-title">
              MOSAIC — Content Integrity Catalogue
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">
              Live proof surface. For every question in a selected grade × modality, this page shows
              the scene image alongside its stimulus, stem, and answer choices. Correct answer in
              green. Use during the district presentation to demonstrate image-to-question congruence.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              disabled={cheatBusy}
              onClick={() => download('/api/content-integrity/internal-stack-cheatsheet.pdf', 'mosaic-internal-stack-cheatsheet.pdf')}
              data-testid="download-internal-cheatsheet-btn"
              title="Engineering only — do NOT hand to district reviewers"
            >
              {cheatBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <FileText className="mr-2 h-4 w-4"/>}
              Internal Stack Cheatsheet
            </Button>
            <Button
              variant="outline"
              disabled={auditBusy}
              onClick={() => download('/api/content-integrity/terminology-audit.pdf', 'mosaic-terminology-audit.pdf')}
              data-testid="download-terminology-audit-btn"
            >
              {auditBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <FileText className="mr-2 h-4 w-4"/>}
              Terminology Audit PDF
            </Button>
            <Button
              disabled={pdfBusy || loading}
              onClick={() => download('/api/content-integrity/catalogue.pdf', `mosaic-catalogue-${grade}-${modality}.pdf`)}
              data-testid="download-catalogue-pdf-btn"
            >
              {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Download className="mr-2 h-4 w-4"/>}
              Download Catalogue PDF
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center mb-6 bg-white p-4 border border-slate-200 rounded">
          <label className="text-sm font-medium text-slate-700">Grade</label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="w-32" data-testid="ci-grade-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GRADES.map(g => <SelectItem key={g} value={g} data-testid={`ci-grade-${g}`}>Grade {g}</SelectItem>)}
            </SelectContent>
          </Select>

          <label className="text-sm font-medium text-slate-700 ml-4">Modality</label>
          <Select value={modality} onValueChange={setModality}>
            <SelectTrigger className="w-36" data-testid="ci-modality-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODALITIES.map(m => <SelectItem key={m} value={m} data-testid={`ci-modality-${m}`}>{m}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="ml-auto text-sm text-slate-500" data-testid="ci-count">
            {loading ? 'Loading…' : `Showing ${items.length} of ${total} items`}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto"/></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="ci-empty">No items for this filter</div>
        ) : (
          <div className="space-y-4">
            {items.map((q, i) => (
              <CatalogueRow key={q.id} q={q} idx={i + 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CatalogueRow({ q, idx }) {
  const [imgOk, setImgOk] = useState(true);
  return (
    <div className="bg-white border border-slate-200 rounded p-4 shadow-sm" data-testid={`ci-row-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase text-slate-500 tracking-wide">
          #{idx} · Grade {q.grade} · {q.modality} · <span className="font-mono">{q.id.slice(0, 12)}</span>
        </div>
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`ci-verdict-${idx}`}>
          {q.verdict || 'PASS'}
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        <div className="border border-slate-200 bg-slate-50 rounded overflow-hidden flex items-center justify-center" style={{ minHeight: 210 }}>
          {q.image_url && imgOk ? (
            <img
              src={q.image_url}
              alt="scene"
              onError={() => setImgOk(false)}
              className="w-full h-full object-cover"
              data-testid={`ci-image-${idx}`}
            />
          ) : (
            <div className="text-slate-400 flex flex-col items-center gap-1 p-4">
              <ImageOff className="h-6 w-6"/>
              <span className="text-xs">no image</span>
            </div>
          )}
        </div>
        <div>
          {q.passage_title && (
            <div className="text-sm mb-2"><span className="font-semibold text-slate-700">Passage:</span> {q.passage_title}</div>
          )}
          {q.stimulus && (
            <div className="text-xs text-slate-600 mb-3 bg-slate-50 p-2 border-l-2 border-slate-300" data-testid={`ci-stimulus-${idx}`}>
              <span className="font-semibold text-slate-700">Stimulus:</span> {q.stimulus.slice(0, 500)}
              {q.stimulus.length > 500 && '…'}
            </div>
          )}
          <div className="text-sm font-semibold text-slate-800 mb-2" data-testid={`ci-stem-${idx}`}>
            Q: {q.stem}
          </div>
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => (
              <div
                key={oi}
                className={
                  'flex items-start gap-2 text-sm px-2 py-1.5 rounded ' +
                  (opt.is_correct
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium'
                    : 'bg-white text-slate-600 border border-slate-100')
                }
                data-testid={`ci-option-${idx}-${oi}`}
              >
                {opt.is_correct
                  ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0"/>
                  : <Circle className="h-4 w-4 mt-0.5 text-slate-300 flex-shrink-0"/>}
                <span>{opt.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
